import React from 'react';
import { motion } from "framer-motion";
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const DeliveryPartnerTerms = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
        {/* Animated Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <motion.div
            animate={{ x: [0, 40, 0], y: [0, 60, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
            className="absolute top-[10%] -left-[5%] w-[35%] h-[35%] bg-primary/5 rounded-full blur-[100px]"
          />
          <motion.div
            animate={{ x: [0, -70, 0], y: [0, 90, 0] }}
            transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[20%] -right-[5%] w-[30%] h-[30%] bg-accent/5 rounded-full blur-[100px]"
          />
        </div>

        <section id="delivery-partner-terms" className="relative z-10 pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              {/* Header */}
              <div className="text-center mb-16 space-y-4">
                <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
                  Partner <span className="text-primary">Terms</span>
                </h1>
                <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs">Agreement • Delivery • Partnership</p>
                <p className="text-muted-foreground mt-2">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-muted/50 rounded-2xl p-8 md:p-12 space-y-8 text-foreground border border-border">
                
                {/* Introduction */}
                <div className="p-4 bg-background border border-border rounded-lg mb-8">
                  <p className="text-muted-foreground"><strong>Platform:</strong> Quro (Website & Mobile Application)</p>
                  <p className="text-muted-foreground"><strong>Operator:</strong> Quick Retail Online Private Limited (“Quro”, “We”, “Us”, “Our”)</p>
                  <p className="text-muted-foreground text-sm mt-1"><strong>FSSAI Reg. No.:</strong> 30260424124243466</p>
                  <p className="text-muted-foreground mt-2 text-sm">This Delivery Partner Agreement ("Agreement") governs the relationship between Quro and the independent contractor ("Delivery Partner", "Partner", "You") providing delivery services through our platform.</p>
                </div>

                {/* 1. PURPOSE OF AGREEMENT */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">1. PURPOSE OF AGREEMENT</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    This Agreement sets out the terms under which Quro will provide a digital platform for the Delivery Partner to receive delivery requests and facilitate the transport of goods from Vendors to Customers.
                  </p>
                </div>

                {/* 2. PARTNER OBLIGATIONS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">2. PARTNER OBLIGATIONS</h2>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">2.1 Documentation & Eligibility</h3>
                  <p className="leading-relaxed mb-4 text-muted-foreground">The Delivery Partner must:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Be at least 18 years of age.</li>
                    <li>Possess a valid driving license (if using a motorized vehicle).</li>
                    <li>Provide valid identification, address proof, and bank account details.</li>
                    <li>Ensure all vehicle-related documents (RC, Insurance, PUC) are current and valid.</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">2.2 Equipment & Vehicle</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Maintain your vehicle in good working condition and safety standards.</li>
                    <li>Possess a functional smartphone with the Quro Partner App installed.</li>
                    <li>Use appropriate delivery bags/containers provided or approved by Quro to maintain food temperature and quality.</li>
                  </ul>
                </div>

                {/* 3. SERVICE STANDARDS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">3. SERVICE STANDARDS</h2>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">3.1 Order Picking & Handling</h3>
                  <p className="leading-relaxed mb-4 text-muted-foreground">You agree to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Verify order contents against the invoice at the Vendor's location.</li>
                    <li>Handle packages with care to prevent damage or leakage.</li>
                    <li>Maintain hygiene and cleanliness throughout the delivery process.</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">3.2 Delivery Timelines</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Endeavor to deliver orders within the estimated time provided by the app.</li>
                    <li>Follow the most efficient routes provided by the navigation system.</li>
                    <li>Promptly update status changes (Arrived at Vendor, Picked Up, Arrived at Customer, Delivered) in the app.</li>
                  </ul>
                </div>

                {/* 4. CODE OF CONDUCT */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">4. CODE OF CONDUCT</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Professionalism is key to our partnership. You agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Be courteous and respectful to Vendors, Customers, and Quro support staff.</li>
                    <li>Not consume alcohol or any intoxicating substances while performing deliveries.</li>
                    <li>Adhere to all traffic rules and regulations.</li>
                    <li>Maintain personal hygiene and wear the Quro branded attire (if provided) while on duty.</li>
                  </ul>
                </div>

                {/* 5. EARNINGS & PAYMENTS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">5. EARNINGS & PAYMENTS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Details of pay structure, including base pay, distance-based pay, and incentives, are specified in the Quro Partner App. Payments will be settled into your registered bank account according to the defined cycle (e.g., Weekly).
                  </p>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Quro reserves the right to deduct amounts for damaged goods, customer refunds caused by partner negligence, or violations of service standards.
                  </p>
                </div>

                {/* 6. INDEPENDENT CONTRACTOR STATUS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">6. INDEPENDENT CONTRACTOR STATUS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    You acknowledge that you are an independent contractor and NOT an employee of Quro or Quick Retail Online Private Limited. You are responsible for your own taxes, insurance, and vehicle maintenance.
                  </p>
                </div>

                {/* 7. DATA PRIVACY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">7. DATA PRIVACY</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    You agree to keep Customer information (name, phone number, address) confidential and use it only for the purpose of completing the delivery. Storing or using customer data for any other purpose is strictly prohibited.
                  </p>
                </div>

                {/* 8. TERMINATION */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">8. TERMINATION</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Either party may terminate this partnership at any time. Quro may deactivate your account immediately for:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Fraudulent activity or tampering with the App.</li>
                    <li>Misbehavior or harassment of Customers/Vendors.</li>
                    <li>High rate of order cancellations or delayed deliveries.</li>
                    <li>Violation of traffic or safety laws.</li>
                  </ul>
                </div>

                {/* 9. GOVERNING LAW */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">9. GOVERNING LAW</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    This Agreement is governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana.
                  </p>
                </div>

                {/* 10. CONTACT INFORMATION */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">10. CONTACT US</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    For any queries regarding your partnership or these terms, please contact our Partner Support:
                  </p>
                  <div className="mt-4 p-4 bg-background border border-border rounded-lg">
                    <p className="font-semibold text-foreground">Quro Partner Support</p>
                    <p className="text-muted-foreground">Email: <a href="mailto:q@quro.today" className="text-primary hover:text-primary/80 transition-colors">q@quro.today</a></p>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">Registered Office:</p>
                      <p>Quick Retail Online Private Limited</p>
                      <p>24-21, Ashoknagar</p>
                      <p>Ramachandra Puram, Hyderabad-502032</p>
                    </div>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">HQ Office:</p>
                      <p>Suite 406, 4th floor, MyScape Weave</p>
                      <p>Financial District, Nankramaguda</p>
                      <p>Hyderabad-500032</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Back to Top Button */}
            <div className="mt-12 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <span>Back to Top</span>
              </button>
            </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default DeliveryPartnerTerms;
